// Third party
import React, { useState } from 'react';
import { connect } from 'react-redux';
import Dropzone, { Accept } from 'react-dropzone';
import parse from 'html-react-parser';

// Actions
import { ReduxState } from '@/redux/modules/types';
import { getFileUploadUrl, addDataUploadFile, getDataUploadPortalConfigs } from '@/redux/modules/enterprise/admin/data-upload';

// Components
import { PrimaryLink } from '@/views/components/buttons';
import FileUploadResult from './FileUploadResult';

// Types
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import { DataUploadPortalConfig, DataUploadPortalFile } from '@/types/data-upload-config';

// Utils
import { FormattedMessage, formatBytes, formatIterationWithConjunction } from '@/utils/i18n';
import { FILE_UPLOAD_SPECS, FILE_EXTENSIONS_MIME } from '@/utils/constants';

type DispatchProps = {
  addDataUploadFile: (url: string, configKey: string, assetKey: string) => Promise<void>;
  getFileUploadUrl: (file: File) => Promise<string>;
  getDataUploadPortalConfigs: () => Promise<Array<DataUploadPortalConfig>>;
};

type StateProps = {
  locale: Locale;
};

type Props = StateProps &
  DispatchProps & {
    configKey: string;
    description: LocalizedString;
    file: DataUploadPortalFile;
    title: string;
    onReplaceFile: (replaced: boolean) => void;
  };

const FileUpload: React.FC<Props> = (props: Props) => {
  const { configKey, description, file, locale, title, addDataUploadFile, getDataUploadPortalConfigs, getFileUploadUrl, onReplaceFile } = props;
  const isUploadedFile = file.createdAt && file.url ? file : null;

  const [error, setError] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<Array<File> | any>(isUploadedFile);

  const onDropAccepted = async (dropzoneFiles: File[]) => {
    const fileToUpload = dropzoneFiles[0];
    const fileUrl = await getFileUploadUrl(fileToUpload);
    const assetKey = file.key;
    await addDataUploadFile(fileUrl, configKey, assetKey);
    await getDataUploadPortalConfigs();
  };

  const isValidateFileSize = (file: File, size: number) => {
    setUploadedFile(file);
    if (file.size > size) {
      setError('Admin.DataUpload.FileTooLarge');
      return {
        code: 'Admin.DataUpload.FileTooLarge',
        message: `file is larger than ${size} bytes`,
      };
    }

    onReplaceFile(false);
    setError('');
    return null;
  };

  /**
   * Get the accepted file mimes and extensions for the file upload. If the file has no specified extensions,
   * it will return the default list of supported file extensions. Results used for the DropZone file validation.
   * @param {DataUploadPortalFile} file - The file object
   * @returns {string} The localized list of accepted file extensions to display
   */
  const getAcceptedFileTypes = (file: DataUploadPortalFile): Accept => {
    const extensions = file.extensions && file.extensions.length > 0 ? file.extensions : FILE_UPLOAD_SPECS.supportedFileExtensions;
    const acceptedFileTypes: Accept = {};
    for (const ext of extensions) {
      const mime = FILE_EXTENSIONS_MIME[ext];
      if (!acceptedFileTypes[mime]) {
        acceptedFileTypes[mime] = [];
      }
      acceptedFileTypes[mime].push(ext);
    }
    return acceptedFileTypes;
  };

  /**
   * Get the accepted file extensions for the file upload. If the file has no specified extensions,
   * it will return the default list of supported file extensions.
   * @param {DataUploadPortalFile} file - The file object
   * @returns {string} The localized list of accepted file extensions to display
   */
  const getAcceptedFileExtensions = (file: DataUploadPortalFile): string => {
    if (!file.extensions || file.extensions.length === 0) {
      return formatIterationWithConjunction(
        FILE_UPLOAD_SPECS.supportedFileExtensions.map(e => e.toString()),
        'or',
      );
    }
    return formatIterationWithConjunction(
      file.extensions.map(e => e.toString()),
      'or',
    );
  };

  const renderDescription = (descriptionLocale: string | undefined) => {
    // This gets the links from the description string and creates a description element with link inside and is returned by the function
    // It uses regex for a format like MD like [Placeholder](link)
    const descriptionLink =
      '<a href="$2" target="_blank"><button type="button" className="primary-link"><span>$1</span><i className="icon-expand-top-right"></i></button></a>';
    const replacedDescription = descriptionLocale?.replace(/\[(.*?)\]\((.*?)\)/g, descriptionLink);
    return <div>{parse(`<div className="file-description">${replacedDescription}</div>`)}</div>;
  };

  const replaceFile = () => {
    setUploadedFile(false);
    onReplaceFile(true);
  };

  return (
    <div className="file-upload">
      <span className="file-title">{title}</span>
      {uploadedFile ? (
        <FileUploadResult error={error} file={uploadedFile} replaceFile={replaceFile} />
      ) : (
        <Dropzone
          onDropAccepted={onDropAccepted}
          accept={getAcceptedFileTypes(file)}
          multiple={false}
          noClick
          noKeyboard
          validator={(file: File) => isValidateFileSize(file, FILE_UPLOAD_SPECS.maxSize)}
        >
          {({ getRootProps, getInputProps, open }) => (
            <div {...getRootProps()} className="dropzone">
              <input {...getInputProps()} />
              <div className="dropzone-cta">
                <div className="upload-icon d-flex flex-column">
                  <i className="icon-upload-new" />
                </div>
                <div className="image-drop-text">
                  <PrimaryLink onClick={open}>
                    <span className="me-2">
                      <FormattedMessage id="Modal.AdminDataUpload.ClickToUpload" />
                    </span>
                  </PrimaryLink>
                  <FormattedMessage
                    id="Modal.AdminDataUpload.DragDrop"
                    values={{
                      extensions: getAcceptedFileExtensions(file),
                    }}
                  />
                </div>
                <span className="file-uploader-dropzone-limit">
                  <FormattedMessage id="Modal.AdminDataUpload.SizeLimit" values={{ limit: formatBytes(FILE_UPLOAD_SPECS.maxSize) }} />
                </span>
              </div>
            </div>
          )}
        </Dropzone>
      )}
      {description && renderDescription(description[locale])}
    </div>
  );
};
const mapStateToProps = (state: ReduxState) => ({
  locale: state.intl.locale,
});

export default connect(mapStateToProps, {
  addDataUploadFile,
  getDataUploadPortalConfigs,
  getFileUploadUrl,
})(FileUpload);
